<script>
import { range } from "lodash-es";
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref, toRefs } from "vue";
import Draggable from "vuedraggable";

import TgButton from "@/abc-dsk/components/Button/TgButton.vue";
import { useDragAndDropKeyboard } from "@/common/composables/use-drag-and-drop-keyboard";
import { AnswerStateEnum, QuizStateEnum } from "@/common/composables/use-vocab-quiz";
import { isBackspace, isComboKey } from "@/common/event-helpers";
import IconCorrect from "@/components/icons/IconCorrect.vue";
import IconIncorrect from "@/components/icons/IconIncorrect.vue";
import { Autofocus } from "@/directives";
import { i18n } from "@/i18n";

import { useGameComponent } from "./common";

const { commonProps, commonEmits } = useGameComponent();

export default defineComponent({
  components: { Draggable, IconCorrect, IconIncorrect, TgButton },
  directives: { Autofocus },
  props: commonProps,
  emits: commonEmits,
  /** @param {import("./common").Props} props */
  setup(props, { emit }) {
    const { t } = i18n.global;
    const { promptId, questions, quizState } = toRefs(props);

    const { onKey, resetAccentBuffer } = useDragAndDropKeyboard();

    const state = ref(null);
    const hasAnswered = computed(() =>
      [AnswerStateEnum.Correct, AnswerStateEnum.Incorrect, AnswerStateEnum.Pending].includes(
        state.value
      )
    );

    const question = computed(() => questions.value[0]);

    const options = ref(
      question.value.options.map((letter, i) => ({
        text: letter,
        key: i
      }))
    );
    const inputLetters = ref([]);
    const placeholdersRef = ref(null);
    const placeholders = ref(range(0, question.value.lemma.length));
    const inputLettersStyle = computed(() => {
      if (!placeholdersRef.value) {
        return {};
      }

      const boundingRect = placeholdersRef.value.getBoundingClientRect();

      return {
        width: `${boundingRect.width}px`,
        height: `${boundingRect.height}px`
      };
    });

    const sanitizedAnswer = computed(() =>
      inputLetters.value.map((option) => option.text).join("")
    );

    const isInputFull = computed(() => inputLetters.value.length === placeholders.value.length);

    const isSubmitDisabled = computed(() => {
      if (!isInputFull.value) {
        return true;
      }

      if (hasAnswered.value) {
        return true;
      }

      return QuizStateEnum.Answered === quizState.value;
    });

    const onAnswer = async () => {
      if (!isInputFull.value) {
        return;
      }

      resetAccentBuffer();

      const answer = sanitizedAnswer.value;
      state.value = AnswerStateEnum.Pending;

      state.value = await props.answerValidator(question.value.id, answer);
      emit("answer", answer);
    };

    const moveOptionLetter = (item) => {
      if (isInputFull.value) {
        return;
      }

      if (hasAnswered.value) {
        return;
      }

      options.value = options.value.filter((i) => i.key !== item.key);
      inputLetters.value = [...inputLetters.value, item];
    };

    const moveInputLetter = (item) => {
      if (hasAnswered.value) {
        return;
      }

      inputLetters.value = inputLetters.value.filter((i) => i.key !== item.key);
      options.value = [...options.value, item];
    };

    const onChange = (e) => {
      if (!e.added) {
        return;
      }

      if (inputLetters.value.length <= placeholders.value.length) {
        return;
      }

      // Move extra input-letter back to options
      const item = inputLetters.value[inputLetters.value.length - 1];
      inputLetters.value = inputLetters.value.filter((i) => i.key !== item.key);
      options.value = [...options.value, item];
    };

    const optionsWithActiveLetters = computed(() =>
      [
        ...options.value.map((i) => ({ ...i, active: false })),
        ...inputLetters.value.map((i) => ({ ...i, active: true }))
      ].sort((a, b) => a.key - b.key)
    );

    const inputAnswer = computed(() =>
      sanitizedAnswer.value.padEnd(placeholders.value.length, "_")
    );

    const toggleInputLetter = (item) => {
      if (hasAnswered.value) {
        return;
      }

      if (options.value.find((i) => i.key === item.key)) {
        options.value = options.value.filter((i) => i.key !== item.key);
        inputLetters.value = [...inputLetters.value, item];
      } else {
        inputLetters.value = inputLetters.value.filter((i) => i.key !== item.key);
        options.value = [...options.value, item];
      }
    };

    const inputStateClass = computed(() => {
      if (state.value === AnswerStateEnum.Correct) {
        return "input--correct";
      }

      if (state.value === AnswerStateEnum.Incorrect) {
        return "input--incorrect";
      }

      return "";
    });

    const submitBtn = ref(null);

    /** @param {KeyboardEvent} e */
    const handleKeyboardInput = async (e) => {
      const key = onKey(e);

      if (!key) {
        return;
      }

      const item = options.value.find((i) => i.text.toLowerCase() === key.toLowerCase());
      if (!item) {
        return;
      }

      if (isInputFull.value) {
        return;
      }

      options.value = options.value.filter((i) => i.key !== item.key);
      inputLetters.value = [...inputLetters.value, item];

      if (isInputFull.value) {
        // Can't focus submit before another tick o.O
        await nextTick();

        submitBtn.value?.$el.focus();
      }
    };

    /** @param {KeyboardEvent} e */
    const handleDeleteInput = (e) => {
      if (isComboKey(e) || !isBackspace(e)) {
        return;
      }

      if (hasAnswered.value) {
        return;
      }

      if (inputLetters.value.length === 0) {
        return;
      }

      const item = inputLetters.value.at(-1);
      inputLetters.value = inputLetters.value.slice(0, -1);
      options.value = [...options.value, item];
    };

    onMounted(() => {
      window.addEventListener("keydown", handleDeleteInput);
      window.addEventListener("keyup", handleKeyboardInput);
    });
    onBeforeUnmount(() => {
      window.removeEventListener("keydown", handleDeleteInput);
      window.removeEventListener("keyup", handleKeyboardInput);
    });

    return {
      t,
      AnswerStateEnum,

      moveOptionLetter,
      moveInputLetter,
      placeholders,
      placeholdersRef,
      inputLetters,
      inputLettersStyle,
      options,
      onChange,

      inputAnswer,
      optionsWithActiveLetters,
      inputStateClass,
      isInputFull,
      toggleInputLetter,

      submitBtn,

      state,
      promptId,
      hasAnswered,
      onAnswer,
      isSubmitDisabled
    };
  }
});
</script>

<template>
  <form class="row" @submit.prevent="onAnswer()">
    <!-- Desktop version -->
    <div class="col-12 d-none d-md-block">
      <div class="row">
        <div class="col-12 d-flex justify-content-center mb-5">
          <div class="answer-container">
            <div class="input-container">
              <div ref="placeholdersRef" class="letter-group letter-group--placeholder">
                <div
                  v-for="placeholder in placeholders"
                  :key="placeholder"
                  class="letter letter--placeholder"
                ></div>
              </div>
              <Transition name="fade">
                <p
                  v-if="placeholders.length > 1 && inputLetters.length === 0"
                  class="drag-drop-container-label"
                >
                  {{ t("vocab.games.dragDropLabel") }}
                </p>
              </Transition>
              <Draggable
                class="letter-group letter-group--input"
                :list="inputLetters"
                :style="inputLettersStyle"
                :disabled="hasAnswered"
                :force-fallback="true"
                group="letters"
                chosen-class="letter-dragging"
                drag-class="letter-dragging"
                ghost-class="letter-ghost"
                item-key="key"
                direction="horizontal"
                :animation="200"
                @change="onChange($event)"
              >
                <template #item="{ element: item }">
                  <article>
                    <button
                      class="letter letter--draggable"
                      :disabled="hasAnswered"
                      :class="{
                        'letter--pending': state === AnswerStateEnum.Pending,
                        'letter--correct': state === AnswerStateEnum.Correct,
                        'letter--incorrect': state === AnswerStateEnum.Incorrect,
                        'letter--lowercase': item.text === 'ß'
                      }"
                      @click.prevent="moveInputLetter(item)"
                    >
                      {{ item.text }}
                    </button>
                  </article>
                </template>
              </Draggable>
            </div>

            <Transition name="spiral">
              <IconCorrect
                v-if="state === AnswerStateEnum.Correct"
                class="answer-result-icon answer-result-icon--correct"
              />
              <IconIncorrect
                v-else-if="state === AnswerStateEnum.Incorrect"
                class="answer-result-icon answer-result-icon--incorrect"
              />
            </Transition>
            <span v-if="state === AnswerStateEnum.Correct" class="sr-only" aria-live="polite">
              {{ t("vocab.quizSelectorButton.correct") }}
            </span>
            <span
              v-else-if="state === AnswerStateEnum.Incorrect"
              class="sr-only"
              aria-live="polite"
            >
              {{ t("vocab.quizSelectorButton.incorrect") }}
            </span>
          </div>
        </div>
        <div class="col col-md-8 offset-md-2">
          <Draggable
            class="letter-group"
            :list="options"
            :disabled="hasAnswered"
            :force-fallback="true"
            group="letters"
            chosen-class="letter-dragging"
            drag-class="letter-dragging"
            ghost-class="letter-ghost"
            item-key="key"
            :animation="200"
          >
            <template #item="{ element: item }">
              <article>
                <button
                  class="letter letter--draggable"
                  :class="{ 'letter--lowercase': item.text === 'ß' }"
                  :disabled="hasAnswered"
                  @click.prevent="moveOptionLetter(item)"
                >
                  {{ item.text }}
                </button>
              </article>
            </template>
          </Draggable>
        </div>
      </div>
    </div>

    <!-- Mobile version -->
    <div class="col-12 d-md-none">
      <div class="row">
        <div class="col-12">
          <div class="mobile-input-container">
            <input
              type="text"
              class="input"
              :value="inputAnswer"
              :aria-labelledby="promptId"
              :class="inputStateClass"
              readonly
            />
            <Transition name="spiral">
              <IconCorrect
                v-if="state === AnswerStateEnum.Correct"
                class="answer-result-icon answer-result-icon--correct"
              />
              <IconIncorrect
                v-else-if="state === AnswerStateEnum.Incorrect"
                class="answer-result-icon answer-result-icon--incorrect"
              />
            </Transition>
            <span v-if="state === AnswerStateEnum.Correct" class="sr-only" aria-live="polite">
              {{ t("vocab.quizSelectorButton.correct") }}
            </span>
            <span
              v-else-if="state === AnswerStateEnum.Incorrect"
              class="sr-only"
              aria-live="polite"
            >
              {{ t("vocab.quizSelectorButton.incorrect") }}
            </span>
          </div>
        </div>
        <div class="col-12">
          <div class="letter-group">
            <button
              v-for="(optionsWithActiveLetter, index) in optionsWithActiveLetters"
              :key="index"
              class="letter"
              :class="{
                'letter--active': !hasAnswered && optionsWithActiveLetter.active,
                'letter--inactive': !hasAnswered && isInputFull && !optionsWithActiveLetter.active,
                'letter--correct':
                  hasAnswered &&
                  optionsWithActiveLetter.active &&
                  state === AnswerStateEnum.Correct,
                'letter--incorrect':
                  hasAnswered &&
                  optionsWithActiveLetter.active &&
                  state === AnswerStateEnum.Incorrect
              }"
              :disabled="hasAnswered || (isInputFull && !optionsWithActiveLetter.active)"
              @click.prevent="toggleInputLetter(optionsWithActiveLetter)"
            >
              {{ optionsWithActiveLetter.text }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 text-center">
      <TgButton
        ref="submitBtn"
        class="submit-button"
        size="lg"
        type="submit"
        :disabled="isSubmitDisabled"
      >
        {{ t("vocab.games.confirm") }}
      </TgButton>
    </div>
  </form>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

@icon-size: 38px;
@letter-height: 75px;
@letter-border-width: 1px;
@input-container-padding: 14px;

.flip-list-move {
  transition: transform 0.5s;
}
.no-move {
  transition: transform 0s;
}

.answer-container {
  position: relative;
  padding: 0 (@icon-size + 20px);
}

.input-container {
  position: relative;
  width: 100%;
  max-width: calc(100vw - 30px);
  padding: @input-container-padding;
  overflow: hidden;
  overflow-x: auto;
  border: 1px solid rgb(@theme-colors[@grey][50]);
  border-radius: 8px;
}

.drag-drop-container-label {
  position: absolute;
  top: 50%;
  left: 50%;
  margin-bottom: 0;
  overflow: hidden;
  transform: translate(-50%, -50%);
  color: rgb(@theme-colors[@grey][base]);
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5rem;
  white-space: nowrap;
  user-select: none;
}

.letter-group {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 14px;

  &--placeholder {
    z-index: 1;
    flex-wrap: nowrap;
    justify-content: flex-start;
  }

  &--input {
    position: absolute;
    z-index: 2;
    top: @input-container-padding;
    left: @input-container-padding;
    flex-wrap: nowrap;
    justify-content: flex-start;
  }
}

.letter-ghost {
  > .letter {
    transform: none !important;
    border-color: rgb(@theme-colors[@grey][base]);
    background-color: rgb(@theme-colors[@grey][50]);
  }
}

.letter-dragging {
  > .letter {
    transform: rotate(-14deg);
  }
}

.letter {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 60px;
  min-width: 60px;
  height: @letter-height;
  min-height: @letter-height;
  padding: 18px 20px;
  transition: all 150ms ease-in-out;
  border: @letter-border-width solid rgb(@theme-colors[@grey][50]);
  border-radius: 4px;
  background-color: rgb(@theme-colors[@grey][10]);
  color: @colors[@black];
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.75rem;
  text-align: center;
  text-transform: uppercase;
  user-select: none;

  &:hover:not(:disabled):not(.letter--placeholder),
  &:active:not(:disabled):not(.letter--placeholder) {
    border-color: rgb(@theme-colors[@grey][base]);
  }

  &--lowercase {
    text-transform: lowercase;
  }

  &--placeholder {
    border-color: rgb(@theme-colors[@grey][10]);
    background-color: rgb(@theme-colors[@grey][10]);
  }

  &--draggable {
    cursor: move;
  }

  &--inactive {
    opacity: 0.5;
  }

  &--active,
  &--pending {
    border-color: rgb(@theme-colors[@secondary][base]);
    background: rgb(@theme-colors[@secondary][base]);
    color: @colors[@white];
  }

  &--correct {
    border-color: rgb(@theme-colors[@primary][base]);
    background: rgb(@theme-colors[@primary][base]);
    color: @colors[@white];
  }

  &--incorrect {
    border-color: rgb(@theme-colors[@red][base]);
    background: rgb(@theme-colors[@red][10]);
    color: rgb(@theme-colors[@red][base]);
  }
}

.answer-result-icon {
  width: @icon-size;
  height: @icon-size;
  margin-right: auto;

  #media-breakpoint-up(md, {
    position: absolute;
    top: (((@input-container-padding * 2) + (@letter-border-width * 2) + @letter-height) / 2) -
      (@icon-size / 2);
    right: 0;
  });

  &--correct {
    color: rgb(@theme-colors[@primary][50]);
  }

  &--incorrect {
    color: rgb(@theme-colors[@red][base]);
  }
}

.mobile-input-container {
  @grid-gap: 20px;
  display: grid;
  grid-template-columns: @icon-size auto @icon-size;
  align-items: center;
  justify-items: center;
  gap: @grid-gap;
  width: 100%;
  max-width: 375px + (@grid-gap + @icon-size) * 2;
  margin: 0 auto 20px;
}

.input {
  grid-column-start: 2;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid rgb(@theme-colors[@grey][50]);
  border-radius: 4px;
  outline: none;
  background: white;
  background-color: @colors[@white];
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5rem;
  cursor: not-allowed;
  pointer-events: none;
  user-select: none;

  &::placeholder {
    color: rgb(@theme-colors[@grey][50]);
  }

  &--correct {
    border-color: rgb(@theme-colors[@primary][base]);
    box-shadow: 0px 0px 0px 4px rgba(@theme-colors[@primary][base], 0.3);
  }

  &--incorrect {
    border-color: rgb(@theme-colors[@red][base]);
    box-shadow: 0px 0px 0px 4px rgba(@theme-colors[@red][base], 0.3);
  }
}

.submit-button {
  margin-top: 30px;

  #media-breakpoint-up(md, {
      margin-top: 45px;
  });
}

// Animation
.spiral-enter-active {
  transition: all 200ms ease-out;
}

.spiral-leave-active {
  transition: all 200ms cubic-bezier(1, 0.5, 0.8, 1);
}

.spiral-enter-from,
.spiral-leave-to {
  transform: scale(0.8) rotate(180deg);
  opacity: 0;
}
</style>
